import React, { useState } from "react";

interface ToolFormProps {
  configSchema: any;
  onSubmit: (data: Record<string, any>) => void;
  loading: boolean;
}

function capitalize(s: string): string {
  return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function ToolForm({ configSchema, onSubmit, loading }: ToolFormProps) {
  const properties = configSchema?.properties || {};
  const required: string[] = configSchema?.required || [];

  const buildDefaults = (): Record<string, any> => {
    const defs: Record<string, any> = {};
    for (const [key, schema] of Object.entries<any>(properties)) {
      if (schema.default !== undefined) defs[key] = schema.default;
      else if (schema.type === "boolean") defs[key] = false;
      else if (schema.type === "number") defs[key] = schema.minimum ?? 0;
      else if (schema.type === "array") defs[key] = [];
      else defs[key] = "";
    }
    return defs;
  };

  const [formData, setFormData] = useState<Record<string, any>>(buildDefaults);
  const [arrayInputs, setArrayInputs] = useState<Record<string, string>>({});

  const updateField = (key: string, value: any) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    for (const r of required) {
      const v = formData[r];
      if (v === undefined || v === "" || (Array.isArray(v) && v.length === 0)) {
        alert(capitalize(r) + " is required.");
        return;
      }
    }
    onSubmit(formData);
  };

  const addArrayItem = (key: string) => {
    const val = (arrayInputs[key] || "").trim();
    if (!val) return;
    updateField(key, [...(formData[key] || []), val]);
    setArrayInputs((prev) => ({ ...prev, [key]: "" }));
  };

  const removeArrayItem = (key: string, idx: number) => {
    updateField(key, (formData[key] || []).filter((_: any, i: number) => i !== idx));
  };

  const addObjectArrayItem = (key: string, itemProps: any) => {
    const blank: Record<string, any> = {};
    for (const [pk, ps] of Object.entries<any>(itemProps)) {
      blank[pk] = ps.type === "number" ? 0 : ps.type === "boolean" ? false : "";
    }
    updateField(key, [...(formData[key] || []), blank]);
  };

  const updateObjectArrayItem = (key: string, idx: number, field: string, value: any) => {
    const arr = [...(formData[key] || [])];
    arr[idx] = { ...arr[idx], [field]: value };
    updateField(key, arr);
  };

  const removeObjectArrayItem = (key: string, idx: number) => {
    updateField(key, (formData[key] || []).filter((_: any, i: number) => i !== idx));
  };

  const inputClass = "w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 outline-none bg-white transition placeholder-gray-400";
  const labelClass = "block text-sm font-semibold text-gray-700 mb-1.5";

  const renderField = (key: string, schema: any) => {
    const isReq = required.includes(key);
    const label = (
      <label className={labelClass}>
        {capitalize(key)}
        {isReq && <span className="text-red-500 ml-1">*</span>}
      </label>
    );
    const helper = schema.description ? (
      <p className="text-xs text-gray-500 mt-1">{schema.description}</p>
    ) : null;

    if (schema.type === "string" && schema.enum) {
      return (
        <div key={key} className="mb-5">
          {label}
          <select className={inputClass} value={formData[key] || ""} onChange={(e) => updateField(key, e.target.value)}>
            <option value="">Select…</option>
            {schema.enum.map((opt: string) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
          {helper}
        </div>
      );
    }

    if (schema.type === "string" && schema.format === "textarea") {
      return (
        <div key={key} className="mb-5">
          {label}
          <textarea
            className={inputClass + " min-h-[140px] resize-y leading-relaxed"}
            value={formData[key] || ""}
            onChange={(e) => updateField(key, e.target.value)}
            placeholder={schema.description || ""}
          />
          {helper}
        </div>
      );
    }

    if (schema.type === "string") {
      return (
        <div key={key} className="mb-5">
          {label}
          <input
            type="text"
            className={inputClass}
            value={formData[key] || ""}
            onChange={(e) => updateField(key, e.target.value)}
            placeholder={schema.description || ""}
          />
          {helper}
        </div>
      );
    }

    if (schema.type === "number") {
      return (
        <div key={key} className="mb-5">
          {label}
          <input
            type="number"
            className={inputClass}
            value={formData[key] ?? ""}
            min={schema.minimum}
            max={schema.maximum}
            step={schema.step ?? (schema.minimum !== undefined && schema.minimum < 1 ? 0.01 : 1)}
            onChange={(e) => updateField(key, parseFloat(e.target.value) || 0)}
            placeholder={schema.description || ""}
          />
          {helper}
        </div>
      );
    }

    if (schema.type === "boolean") {
      return (
        <div key={key} className="mb-5 flex items-start gap-3 p-3 bg-gray-50 rounded-xl border border-gray-200">
          <input
            type="checkbox"
            id={key}
            checked={!!formData[key]}
            onChange={(e) => updateField(key, e.target.checked)}
            className="h-4 w-4 mt-0.5 text-indigo-600 rounded accent-indigo-600"
          />
          <div>
            <label htmlFor={key} className="text-sm font-semibold text-gray-700 cursor-pointer">
              {capitalize(key)}{isReq && <span className="text-red-500 ml-1">*</span>}
            </label>
            {schema.description && <p className="text-xs text-gray-400 mt-0.5">{schema.description}</p>}
          </div>
        </div>
      );
    }

    if (schema.type === "array" && schema.items?.type === "string") {
      const items: string[] = formData[key] || [];
      return (
        <div key={key} className="mb-5">
          {label}
          <div className="flex flex-wrap gap-2 mb-2 min-h-[36px]">
            {items.map((item, idx) => (
              <span key={idx} className="inline-flex items-center bg-indigo-50 text-indigo-800 border border-indigo-200 text-sm px-3 py-1 rounded-full">
                {item}
                <button type="button" onClick={() => removeArrayItem(key, idx)} className="ml-2 text-indigo-400 hover:text-red-500 text-base leading-none">&times;</button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              className={inputClass}
              value={arrayInputs[key] || ""}
              placeholder={"Type and press Enter or Add"}
              onChange={(e) => setArrayInputs((p) => ({ ...p, [key]: e.target.value }))}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addArrayItem(key); } }}
            />
            <button
              type="button"
              onClick={() => addArrayItem(key)}
              className="shrink-0 px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-semibold transition"
            >Add</button>
          </div>
          {helper}
        </div>
      );
    }

    if (schema.type === "array" && schema.items?.type === "object") {
      const items: any[] = formData[key] || [];
      const itemProps: Record<string, any> = schema.items.properties || {};
      // Determine number of columns: ≥4 fields → 3 col grid, 2-3 → 2 col, 1 → 1 col
      const fieldCount = Object.keys(itemProps).length;
      const gridClass = fieldCount >= 4
        ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
        : fieldCount >= 2
          ? "grid grid-cols-1 sm:grid-cols-2 gap-4"
          : "space-y-3";
      return (
        <div key={key} className="mb-5">
          {label}
          {helper && <div className="mb-3">{helper}</div>}
          <div className="space-y-4">
            {items.map((item, idx) => (
              <div key={idx} className="border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
                <div className="flex justify-between items-center bg-gradient-to-r from-indigo-50 to-purple-50 border-b border-gray-200 px-5 py-3">
                  <span className="text-sm font-bold text-gray-700">
                    {capitalize(key.replace(/s$/, ""))} {idx + 1}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeObjectArrayItem(key, idx)}
                    className="text-xs text-red-500 hover:text-red-700 font-medium px-3 py-1 rounded-lg hover:bg-red-50 transition"
                  >Remove</button>
                </div>
                <div className={gridClass + " p-5"}>
                  {Object.entries<any>(itemProps).map(([pk, ps]) => (
                    <div key={pk}>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">{capitalize(pk)}</label>
                      {ps.type === "string" && ps.enum ? (
                        <select
                          className={inputClass + " text-sm"}
                          value={item[pk] ?? ""}
                          onChange={(e) => updateObjectArrayItem(key, idx, pk, e.target.value)}
                        >
                          <option value="">Select…</option>
                          {ps.enum.map((opt: any) => <option key={opt} value={opt}>{opt}</option>)}
                        </select>
                      ) : (
                        <input
                          type={ps.type === "number" ? "number" : "text"}
                          className={inputClass + " text-sm"}
                          value={item[pk] ?? ""}
                          min={ps.minimum}
                          max={ps.maximum}
                          step={ps.type === "number" && ps.minimum !== undefined && ps.minimum < 1 ? 0.01 : undefined}
                          placeholder={ps.description || ps.default !== undefined ? String(ps.default ?? "") : ""}
                          onChange={(e) => updateObjectArrayItem(key, idx, pk, ps.type === "number" ? parseFloat(e.target.value) || 0 : e.target.value)}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={() => addObjectArrayItem(key, itemProps)}
            className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-indigo-600 hover:text-indigo-800 px-4 py-2 border border-dashed border-indigo-300 hover:border-indigo-500 rounded-xl transition"
          >
            <span className="text-lg leading-none">+</span> Add {capitalize(key.replace(/s$/, ""))}
          </button>
        </div>
      );
    }

    return (
      <div key={key} className="mb-4">
        {label}
        <input type="text" className={inputClass} value={formData[key] || ""} onChange={(e) => updateField(key, e.target.value)} />
        {helper}
      </div>
    );
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-0">
      {Object.entries<any>(properties).map(([key, schema]) => renderField(key, schema))}
      <div className="pt-2">
        <button
          type="submit"
          disabled={loading}
          className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-3.5 rounded-xl font-bold text-base hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 transition shadow-md hover:shadow-lg flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
              Running…
            </>
          ) : (
            <>
              <span>⚡</span>
              Run Tool
            </>
          )}
        </button>
      </div>
    </form>
  );
}